"use client";

import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa";
import { useRouter } from "@/i18n/navigation";
import { useParams } from "next/navigation";

const Sidebar = ({ isOpen, setIsOpen, menu, data }) => {
  const t = useTranslations("Header");
  const [openServiceIndex, setOpenServiceIndex] = useState<number | null>(null);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const router = useRouter();
  const { locale } = useParams();
  const closeSidebar = () => {
    setIsOpen(false);
  };

  const changeRoute = (path: string, hasChildren: boolean) => {
    if (!hasChildren && path && path !== "#") {
      router.push(path, { locale: locale as string });
      closeSidebar();
      setIsServicesOpen(false);
      setOpenServiceIndex(null);
    } else if (hasChildren) {
      setIsServicesOpen(!isServicesOpen);
      setOpenServiceIndex(null);
    }
  };

  const changeChildRoute = (slug: string) => {
    router.replace("/our-services/" + slug, { locale: locale as string });
    closeSidebar();
    setIsServicesOpen(false);
    setOpenServiceIndex(null);
  };

  const changeSubServiceRoute = (parentSlug: string, subSlug: string) => {
    router.replace(`/our-services/${parentSlug}/${subSlug}`, {
      locale: locale as string,
    });
    closeSidebar();
    setIsServicesOpen(false);
    setOpenServiceIndex(null);
  };

  const toggleSubServices = (index: number) => {
    setOpenServiceIndex(openServiceIndex === index ? null : index);
  };

  return (
    <div
      className={`
        bg-white fixed z-[1000] h-screen overflow-hidden transition-all duration-200 ease-in
        ${isOpen ? "w-[300px]" : "w-0"}
      `}
    >
      <div className="flex items-center justify-between p-8 relative">
        <button
          className="bg-transparent border-none text-gray-700 rounded-full p-2 text-2xl hover:bg-gray-100 transition-colors duration-200 absolute top-4 right-4"
          onClick={closeSidebar}
          aria-label="Close Sidebar"
        >
          <FaTimes />
        </button>
      </div>
      <div className="flex flex-col items-start px-4 overflow-y-auto max-h-[calc(100vh-100px)]">
        {menu.map(({ label, path, children }: any, index: React.Key) => (
          <div key={index} className="w-full">
            <button
              key={index}
              onClick={() => changeRoute(path, !!children)}
              className="
                bg-transparent border-none text-lg py-2 w-full text-left capitalize tracking-wider
                text-gray-700 hover:text-gray-700 transition-colors duration-200
                flex items-center justify-between font-medium
              "
              aria-label={label}
            >
              {t(label)}{" "}
              {children && (
                <span>
                  <FaChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? "rotate-180" : ""}`}
                  />
                </span>
              )}
            </button>
            {children?.length && (
              <div
                className={`
                  overflow-hidden transition-all duration-200 ease-linear
                  ${isServicesOpen ? "max-h-[600px]" : "max-h-0"}
                `}
              >
                {children.map((child: any, childIndex: number) => {
                  const hasSubServices = child?.sub_services?.length > 0;
                  return (
                    <div key={childIndex} className="w-full">
                      <button
                        className="
                          px-4 py-2 text-lg text-left w-full
                          text-gray-600 hover:text-gray-800 hover:bg-gray-50 font-medium
                          transition-colors duration-200 flex items-center justify-between
                        "
                        onClick={() =>
                          hasSubServices
                            ? toggleSubServices(childIndex)
                            : changeChildRoute(child?.slug)
                        }
                        aria-label={child?.title}
                      >
                        {child?.title}
                        {hasSubServices && (
                          <FaChevronDown
                            className={`w-3 h-3 transition-transform duration-200 ${openServiceIndex === childIndex ? "rotate-180" : ""}`}
                          />
                        )}
                      </button>
                      {hasSubServices && (
                        <div
                          className={`
                            overflow-hidden transition-all duration-200 ease-linear
                            ${openServiceIndex === childIndex ? "max-h-96" : "max-h-0"}
                          `}
                        >
                          {child.sub_services.map(
                            (sub: any, subIndex: number) => (
                              <button
                                key={subIndex}
                                className="
                                  px-8 py-1.5 text-base text-left w-full
                                  text-gray-500 hover:text-gray-700 hover:bg-gray-50
                                  transition-colors duration-200
                                "
                                onClick={() =>
                                  changeSubServiceRoute(
                                    child?.slug,
                                    sub?.slug,
                                  )
                                }
                                aria-label={sub?.title}
                              >
                                {sub?.title}
                              </button>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Sidebar;
